import { createAction, createReducer } from "@reduxjs/toolkit"
import _ from "lodash"
import moment from "moment"
import widgetSlice, { AM_FUND_SHARE_SELECTOR_ID } from "../../api/widgetSlice"
import httpClient from "../../api/httpClient"
import history from "../../History"
import { asBoolean } from "../../commons/utils"

const { updateSelectorSelection } = widgetSlice.actions

const GET_FUNDS_RESPONSE = createAction("GET_FUNDS_RESPONSE")
const GET_FUND_DOCUMENTS_RESPONSE = createAction("GET_FUND_DOCUMENTS_RESPONSE")
const SET_SELECTED_FUND = createAction("SET_SELECTED_FUND")
const SET_SELECTED_DATE_OF_GROUP = createAction("SET_SELECTED_DATE_OF_GROUP")
const SET_AVAILABLE_DOCUMENTS_GROUPS = createAction(
  "SET_AVAILABLE_DOCUMENTS_GROUPS"
)

/**
 * Update store with current selected fund share.
 * It also ensures that document.url is synchronize with current selection.
 * Finally it dispatch a widget slice action to trigger refresh of current display Widgets.
 * @param fundShare
 * @returns {(function(*): void)|null}
 */
function selectFund(fundShare) {
  if (!fundShare) {
    return null
  }
  return (dispatch) => {
    // synchronize document.url with current selection
    const { pathname, search } = history.location
    const queryParams = new URLSearchParams(search)
    queryParams.set("isinCode", fundShare.isinCode) // We can't juste do "replace('url?isinCode=newValue')" because some parameters in the url might already exist
    history.replace(`${pathname}?${queryParams.toString()}`)

    // be careful, order is really important here !
    dispatch(
      updateSelectorSelection(AM_FUND_SHARE_SELECTOR_ID, fundShare.jumpId)
    )
    dispatch(SET_SELECTED_FUND(fundShare))
  }
}

function clearFundSelection() {
  return (dispatch) => {
    dispatch(SET_SELECTED_FUND(undefined))
  }
}

function selectDatesOf(date) {
  return SET_SELECTED_DATE_OF_GROUP(date)
}

/**
 * Get all fund shares grouped by fund
 * @returns {function(*): Promise<unknown>}
 */
function fetchFundsGroupByMasterFund(cancelToken) {
  return (dispatch) => {
    httpClient
      .get("/widgets/funds", { cancelToken })
      .then(({ data }) => dispatch(GET_FUNDS_RESPONSE(data)))
  }
}

/**
 * Get fund share by ISIN
 * @returns {function(*, *): (Promise<unknown>)}
 * @param args
 * @param cancelToken
 */
function fetchFundByISIN(args, cancelToken) {
  return (dispatch, getState) => {
    const {
      isinCode,
      withHomePageDocuments,
      withAllShares,
      fundRange,
      lang,
      visitorProfile
    } = args
    const { selectedFund } = getState().am
    if (selectedFund && selectedFund.isinCode === isinCode) {
      return Promise.resolve(selectedFund)
    }
    const withHomePageDocumentsBool = asBoolean(withHomePageDocuments)
    const withAllSharesBool = asBoolean(withAllShares)
    const fundRangeBool = asBoolean(fundRange)

    let url = `/widgets/fund/${isinCode}?withHomePageDocuments=${withHomePageDocumentsBool}&withAllShares=${withAllSharesBool}&fundRange=${fundRangeBool}`
    if (lang) {
      url += `&lang=${lang}`
    }
    if (visitorProfile) {
      url += `&visitorProfile=${visitorProfile}`
    }

    return httpClient
      .get(url, { cancelToken })
      .then(({ data }) => dispatch(selectFund(data)))
  }
}

/**
 * Get fund share documents from selected fund share
 * @returns {function(*, *)}
 */
function fetchFundDocuments(cancelToken) {
  return (dispatch, getState) => {
    const { selectedFund, selectedYear } = getState().am
    if (!selectedFund) {
      return null
    }

    const fromDate = moment(selectedYear).startOf("year").toISOString()
    const toDate = moment(selectedYear).endOf("year").toISOString()
    const queryArgs = `fromDate=${fromDate}&toDate=${toDate}&fundId=${selectedFund._id}`

    return httpClient
      .get(`/api/documents2?${queryArgs}`, { cancelToken })
      .then(({ data }) => {
        const documentsByType = _.groupBy(data, "type")
        const groupsFromResponse = _.keys(documentsByType)
        const sortedGroups = groupsFromResponse.sort()
        dispatch(GET_FUND_DOCUMENTS_RESPONSE(documentsByType))
        dispatch(SET_AVAILABLE_DOCUMENTS_GROUPS(sortedGroups))
      })
  }
}

/**
 * Fetch a list of funds with an optional search query and limit
 * @param cancelToken
 * @param {String} searchQuery
 * @param {Number} limit - maximum number of results
 * @returns {function(): any}
 */
function fetchFunds({ cancelToken, searchQuery, limit } = {}) {
  return async () => {
    let url = "/api/funds"
    const searchParams = {}

    if (searchQuery) {
      searchParams.searchQuery = searchQuery
    }

    if (limit) {
      searchParams.limit = limit
    }

    if (Object.keys(searchParams).length > 0) {
      const queryParameters = new URLSearchParams(searchParams).toString()
      url = url.concat("?", queryParameters)
    }

    const { data } = await httpClient.get(url, { cancelToken })
    return data
  }
}

const initialState = {
  funds: [],
  documents: undefined,
  documentGroups: [],
  selectedFund: undefined,
  selectedFundShare: undefined,
  selectedYear: moment().startOf("year")
}
const reducer = createReducer(initialState, {
  GET_FUNDS_RESPONSE: (state, { payload }) => {
    return { ...state, funds: payload }
  },
  GET_FUND_DOCUMENTS_RESPONSE: (state, { payload }) => {
    return {
      ...state,
      documents: payload
    }
  },
  SET_SELECTED_FUND: (state, { payload }) => {
    return { ...state, selectedFund: payload }
  },
  SET_SELECTED_DATE_OF_GROUP: (state, { payload }) => {
    return { ...state, selectedYear: payload }
  },
  SET_AVAILABLE_DOCUMENTS_GROUPS: (state, { payload }) => {
    return { ...state, documentGroups: payload }
  }
})

export default {
  initialState,
  actions: {
    selectFund,
    selectDatesOf,
    fetchFunds,
    fetchFundByISIN,
    fetchFundsGroupByMasterFund,
    fetchFundDocuments,
    clearFundSelection
  },
  reducer
}
